import { FileDown, Instagram } from "lucide-react";
import { Logo } from "../Logo";
import { scrollToId } from "./Navbar";
import { useLang } from "../../lib/i18n";
import { mediaUrl, parseLinks } from "../../lib/api";

const LINKS = [
  { key: "nav.about", id: "about" },
  { key: "nav.stay", id: "villas" },
  { key: "nav.dining", id: "dining" },
  { key: "nav.experiences", id: "experiences" },
  { key: "nav.offers", id: "offers" },
  { key: "nav.gallery", id: "gallery" },
  { key: "nav.blog", id: "blog" },
  { key: "nav.contact", id: "contact" },
];

export default function Footer({ settings }) {
  const { t } = useLang();
  const footerLinks = parseLinks(settings?.footer_links);
  const socialLinks = String(settings?.social_links || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [label, url] = line.split("|").map((s) => (s || "").trim());
      if (url) return { label, url };
      // tolerate raw URLs like "https://instagram.com/handle"
      if (/^https?:\/\//.test(line)) {
        const pretty = line.includes("instagram.com") ? "Instagram" : line.replace(/^https?:\/\/(www\.)?/, "").split("/")[0];
        return { label: pretty, url: line };
      }
      return null;
    })
    .filter(Boolean);
  const customLinks = parseLinks(settings?.nav_menu);
  const hiddenSet = new Set(String(settings?.hidden_sections || "").split(",").map((s) => s.trim()).filter(Boolean));
  const isHidden = (id) => hiddenSet.has(id) || hiddenSet.has(String(id || "").replace(/^\/#/, "").replace(/^#/, ""));
  const links = (customLinks.length
    ? customLinks.map((l) => ({ id: l.url, label: l.label }))
    : LINKS.map((l) => ({ id: l.id, label: t(l.key) }))
  ).filter((l) => !isHidden(l.id));

  return (
    <footer data-testid="site-footer" className="bg-[#171310] text-[#A0988E] py-16 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-10">
          <div className="max-w-xs">
            <Logo
              logoUrl={settings?.logo_footer_url || settings?.logo_url}
              logoLightUrl={settings?.logo_footer_url ? undefined : settings?.logo_light_url}
              height={settings?.logo_footer_url ? 56 : 34}
            />
            <p className="mt-5 text-sm font-sans font-light leading-relaxed">{t("footer.tagline")}</p>
            {settings?.factsheet_url && (
              <a
                data-testid="footer-factsheet-link"
                href={mediaUrl(settings.factsheet_url)}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-[12px] font-sans font-semibold uppercase tracking-[0.12em] text-[#D8CBB8] hover:text-white transition-colors"
              >
                <FileDown className="w-4 h-4" />
                {t("factsheet.download")}
              </a>
            )}
            {footerLinks.length > 0 && (
              <div data-testid="footer-backlinks" className="mt-5 flex flex-col gap-1.5">
                {footerLinks.map((l, i) => (
                  <a
                    key={i}
                    data-testid={`footer-backlink-${i}`}
                    href={l.url}
                    target="_blank"
                    rel="noopener"
                    className="text-xs font-sans text-[#A0988E] hover:text-[#D8CBB8] transition-colors"
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            )}
          </div>
          <nav className="grid grid-cols-2 gap-x-12 gap-y-3">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`footer-link-${l.id}`}
                onClick={() => scrollToId(l.id)}
                className="text-left text-[13px] font-sans uppercase tracking-[0.14em] hover:text-[#D8CBB8] transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>
          <div className="text-sm font-sans space-y-2">
            <div>{settings?.contact_email}</div>
            <div>{settings?.contact_phone}</div>
            <div className="text-xs text-[#A0988E] max-w-[220px]">{settings?.contact_address}</div>
            {socialLinks.length > 0 && (
              <div data-testid="footer-social-links" className="flex gap-4 pt-2">
                {socialLinks.map((l, i) => (
                  <a
                    key={i}
                    data-testid={`footer-social-${i}`}
                    href={l.url}
                    target="_blank"
                    rel="noopener"
                    aria-label={l.label}
                    className="inline-flex items-center gap-2 text-xs font-sans uppercase tracking-[0.14em] text-[#D3C8B9] hover:text-[#D8CBB8] transition-colors"
                  >
                    {l.url.includes("instagram.com") && <Instagram className="w-4 h-4" />}
                    {l.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="mt-14 pt-7 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-sans text-[#A0988E]">
          <span>© {new Date().getFullYear()} Nusadira Lembongan Villa. {t("footer.rights")}</span>
          <span data-testid="footer-member-of">
            {t("footer.member")}{" "}
            <a
              data-testid="footer-armella-link"
              href="https://armellabali.com"
              target="_blank"
              rel="noreferrer"
              className="text-[#D8CBB8] hover:text-white transition-colors"
            >
              Armella Bali
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}
